// import your three store composables:
import { useMedicalRequestStore } from '../../stores/useMedicalRequestStore';
import { useAdminRequestStore } from '../../stores/useAdminRequestStore';
import { useRepeatPrescriptionRequestStore } from '../../stores/useRepeatPrescriptionRequestStore';

export type StoreId = 'medical-request' | 'admin-request' | 'repeat-prescription-request';

type EndReason = 'timeout' | 'cancel' | 'complete' | string;

type RequestStoreCommon = {
  isNhsVerified: boolean;
  isProxy: boolean;
  endCurrent: (reason?: EndReason) => void | Promise<void>;
  submitAction: (...args: any[]) => Promise<any>; // replace with your real signature
};

export function useRequestStoresCommon() {
  const medical = useMedicalRequestStore();
  const admin = useAdminRequestStore();
  const repeat = useRepeatPrescriptionRequestStore();

  const allStores = {
    'medical-request': medical,
    'admin-request': admin,
    'repeat-prescription-request': repeat,
  } as const;

  function getStore<StoreId extends keyof typeof allStores>(
    id: StoreId,
  ): (typeof allStores)[StoreId] {
    return allStores[id];
  }

  function tryGetStore<StoreId extends keyof typeof allStores>(
    id: StoreId,
  ): (typeof allStores)[StoreId] | null {
    return id in allStores ? allStores[id as StoreId] : null;
  }

  async function endCurrent(id: StoreId) {
    getStore(id).endCurrent();
  }

  async function endAll(reason: EndReason = 'cancel') {
    const results = await Promise.allSettled(
      (Object.values(allStores) as RequestStoreCommon[]).map((s) => s.endCurrent(reason)),
    );

    const rejected = results.filter((r) => r.status === 'rejected');
    if (rejected.length) {
      console.warn('endAll(): some stores failed to endCurrent()', rejected);
    }

    return results;
  }

  return {
    endAll,
    getStore,
    endCurrent,
    tryGetStore,
  };
}
